import { canShareBookUploads } from '../bookSharing';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Text, Badge, FileInput, TextInput, ActionIcon, Tooltip } from '@mantine/core';
import axiosInstance from './../axiosInstance.js';

// "Читать" on a book page: the texts of this book the user may read
// (site editions for everyone, the user's own uploads for them only),
// each with reading progress, plus a private upload form.

const T = {
  ru: {
    read: 'Читать', cont: 'Продолжить', open: 'Открыть', texts: 'Тексты книги', own: 'Ваш текст', site: 'Издание',
    upload: 'Загрузить свой текст', uploadBtn: 'Загрузить', file: 'Файл (.txt, .fb2, .epub, .pdf, до 30 МБ)',
    label: 'Название текста', privacy: 'Загруженный файл видите только вы. Мы не файлообменник: никто другой к нему доступа не получит.',
    share: 'Поделиться', shareTitle: 'Поделиться текстом', sharePick: 'Имя пользователя', shareNone: 'Никого не нашлось', shareDone: (n) => `Готово: у ${n} теперь есть этот текст`, shareFail: 'Не удалось поделиться',
    none: 'Текстов пока нет.', del: 'Удалить', download: 'Скачать файл', delConfirm: 'Удалить этот текст и прогресс чтения?', signin: 'Войдите, чтобы загрузить свой текст.',
    pages: (n) => `${n} стр.`, chapters: (n) => `${n} гл.`, pdfNote: 'из PDF, разметка приблизительная',
    formatHint: 'Лучше всего читается FB2: в нём главы, абзацы, автор и серия заданы точно.',
  },
  en: {
    read: 'Read', cont: 'Continue', open: 'Open', texts: 'Texts of this book', own: 'Your text', site: 'Edition',
    upload: 'Upload your own text', uploadBtn: 'Upload', file: 'File (.txt, .fb2, .epub, .pdf, up to 30 MB)',
    label: 'Text title', privacy: 'Only you can see a file you upload. This is not a file sharing site: nobody else gets access.',
    share: 'Share', shareTitle: 'Share this text', sharePick: 'User name', shareNone: 'Nobody found', shareDone: (n) => `Done: ${n} now has this text`, shareFail: 'Could not share',
    none: 'No texts yet.', del: 'Delete', download: 'Download file', delConfirm: 'Delete this text and your reading progress?', signin: 'Sign in to upload your own text.',
    pages: (n) => `${n} pp.`, chapters: (n) => `${n} ch.`, pdfNote: 'from PDF, approximate layout',
    formatHint: 'FB2 reads best: chapters, paragraphs, author and series are exact.',
  },
};

const BookTexts = ({ art, language, myUser }) => {
  const t = T[language === 'en' ? 'en' : 'ru'];
  const navigate = useNavigate();
  const [texts, setTexts] = useState(null);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0); // upload percent, so a 6 MB file does not look like a hang
  const [error, setError] = useState(null);
  const myId = myUser && myUser.myUserInfo ? myUser.myUserInfo.myUserId : null;
  // Sharing an own upload: the text being shared, the user search, result.
  const [shareFor, setShareFor] = useState(null);
  const [shareQuery, setShareQuery] = useState('');
  const [shareUsers, setShareUsers] = useState([]);
  const [shareState, setShareState] = useState(null); // { done: name } | { failed: true }
  useEffect(() => {
    const q = shareQuery.trim();
    if (!canShareBookUploads(myId) || !shareFor || !q) { setShareUsers([]); return undefined; }
    let cancelled = false;
    const timer = setTimeout(() => {
      axiosInstance.get(`/searchUsers?userName=${encodeURIComponent(q)}&limit=8&allUsers=true`)
        .then((r) => { if (!cancelled) setShareUsers((r.data || []).filter((u) => !myUser || u.id !== myId)); })
        .catch(() => { if (!cancelled) setShareUsers([]); });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [shareQuery, shareFor, myId]); // eslint-disable-line react-hooks/exhaustive-deps
  const openShare = (tx) => { setShareFor(tx); setShareQuery(''); setShareUsers([]); setShareState(null); };
  const shareWith = (u) => {
    if (!canShareBookUploads(myId)) return;
    axiosInstance.post(`/texts/${shareFor.id}/share`, { userId: u.id })
      .then(() => { setShareState({ done: u.userName }); setShareQuery(''); setShareUsers([]); })
      .catch(() => setShareState({ failed: true }));
  };

  const load = () => axiosInstance.get(`/arts/${art.id}/texts`).then(r => setTexts(r.data || [])).catch(() => setTexts([]));
  useEffect(() => { if (art && art.category === 3) load(); }, [art && art.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!art || art.category !== 3 || texts === null) return null;
  if (texts.length === 0 && !myUser) return null;

  const readPath = (tx) => `/arts/${art.id}/read/${tx.id}`;
  const inProgress = texts.find(tx => tx.progress && tx.progress.position > 0);
  const primary = inProgress || texts[0];

  const MAX_BYTES = 30 * 1024 * 1024;
  const ACCEPTED = ['txt', 'fb2', 'epub', 'pdf', 'zip'];
  // Refuse before sending what the server would refuse anyway.
  const precheck = (f) => {
    if (!f) return null;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!ACCEPTED.includes(ext)) return language === 'ru' ? `Формат .${ext} не поддерживается: нужен .txt, .fb2, .epub или .pdf` : `.${ext} is not supported: use .txt, .fb2, .epub or .pdf`;
    if (f.size > MAX_BYTES) return language === 'ru' ? `Файл ${(f.size / 1048576).toFixed(1)} МБ, а лимит 30 МБ` : `The file is ${(f.size / 1048576).toFixed(1)} MB, the limit is 30 MB`;
    if (f.size === 0) return language === 'ru' ? 'Файл пустой' : 'The file is empty';
    return null;
  };
  const onPick = (f) => {
    setFile(f);
    setLabel(f ? Array.from(f.name.replace(/\.(?:txt|fb2|epub|pdf)(?:\.zip)?$|\.zip$/i, '')).slice(0, 120).join('') : '');
    setError(precheck(f));
  };
  const describeError = (e) => {
    const code = e.response && e.response.status;
    const body = e.response && typeof e.response.data === 'string' && !e.response.data.startsWith('<') ? e.response.data.trim() : '';
    if (code === 413) return language === 'ru' ? 'Файл слишком большой для загрузки (лимит 30 МБ)' : 'The file is too large to upload (30 MB limit)';
    if (code === 401) return language === 'ru' ? 'Войдите, чтобы загрузить текст' : 'Sign in to upload';
    if (body) return body;
    if (!e.response) return language === 'ru' ? 'Нет связи с сервером, попробуйте ещё раз' : 'No connection to the server, try again';
    return e.message;
  };
  const upload = () => {
    if (!file) return;
    const pre = precheck(file);
    if (pre) { setError(pre); return; }
    setUploading(true); setError(null); setProgress(0);
    const form = new FormData();
    form.append('file', file);
    form.append('label', label.trim());
    form.append('language', language === 'en' ? 'en' : 'ru');
    axiosInstance.post(`/arts/${art.id}/texts`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (ev) => { if (ev.total) setProgress(Math.round(ev.loaded / ev.total * 100)); },
    })
      .then((r) => { setFile(null); setLabel(''); setTexts(prev => [...(prev || []), r.data]); })
      .catch((e) => setError(describeError(e)))
      .finally(() => setUploading(false));
  };
  // The file goes through axios so the auth header applies; then a blob link.
  const download = (tx) => {
    axiosInstance.get(`/texts/${tx.id}/download`, { responseType: 'blob' }).then((r) => {
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = tx.originalFilename || `${tx.label || 'book'}.${tx.format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }).catch(console.error);
  };
  const remove = (tx) => {
    if (!window.confirm(t.delConfirm)) return;
    axiosInstance.delete(`/texts/${tx.id}`).then(() => setTexts(prev => prev.filter(x => x.id !== tx.id))).catch(console.error);
  };

  const pct = (tx) => (tx.progress && tx.progress.percent > 0 ? `${Math.round(tx.progress.percent)}%` : null);

  return (
    <div className="flex justify-center mt-4">
      <div className="flex items-center gap-2 flex-wrap justify-center">
        {primary && (
          <Button
            size="sm" radius="xl" variant="filled"
            onClick={() => navigate(readPath(primary))}
            leftSection={<img src="/book_icon.png" alt="" style={{ width: 16, height: 16 }} />}
          >
            {inProgress ? `${t.cont} · ${pct(inProgress)}` : t.read}
          </Button>
        )}
        <Button size="sm" radius="xl" variant="default" onClick={() => setOpen(true)}>
          {texts.length > 0 ? `${t.texts} (${texts.length})` : t.texts}
        </Button>
      </div>

      <Modal opened={open} onClose={() => setOpen(false)} title={t.texts} centered size="md">
        {texts.length === 0 && <Text size="sm" c="dimmed" mb="sm">{t.none}</Text>}
        <div className="flex flex-col gap-2">
          {texts.map((tx) => (
            <div key={tx.id} className="flex items-center gap-3 p-2 rounded-lg" style={{ background: 'var(--mantine-color-default)' }}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <Text size="sm" fw={600} className="truncate">{tx.label || tx.originalFilename}</Text>
                  <Badge size="xs" variant="light" color={tx.source === 'upload' ? 'blue' : 'gray'} style={{ textTransform: 'none', flexShrink: 0 }}>
                    {tx.source === 'upload' ? t.own : t.site}
                  </Badge>
                </div>
                <Text size="xs" c="dimmed">
                  {[tx.language, tx.format === 'pdf' ? t.pdfNote : tx.format.toUpperCase(), (tx.chapters.at(-1)?.pageEnd ? t.pages(tx.chapters.at(-1).pageEnd) : t.chapters(tx.chapters.length)), pct(tx)].filter(Boolean).join(' · ')}
                </Text>
              </div>
              <Button size="xs" radius="xl" onClick={() => navigate(readPath(tx))}>
                {tx.progress && tx.progress.position > 0 ? t.cont : t.open}
              </Button>
              {tx.sizeBytes > 0 && (
                <Tooltip label={t.download}>
                  <ActionIcon variant="subtle" color="gray" onClick={() => download(tx)} aria-label={t.download}>⤓</ActionIcon>
                </Tooltip>
              )}
              {tx.source === 'upload' && canShareBookUploads(myId) && tx.ownerUserId === myId && (
                <Tooltip label={t.share}>
                  <ActionIcon variant="subtle" color="gray" onClick={() => openShare(tx)} aria-label={t.share}>↗</ActionIcon>
                </Tooltip>
              )}
              {tx.source === 'upload' && (
                <Tooltip label={t.del}>
                  <ActionIcon variant="subtle" color="gray" onClick={() => remove(tx)} aria-label={t.del}>✕</ActionIcon>
                </Tooltip>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
          <Text size="sm" fw={600} mb={4}>{t.upload}</Text>
          {!myUser ? (
            <Text size="xs" c="dimmed">{t.signin}</Text>
          ) : (
            <div className="flex flex-col gap-2">
              <FileInput size="sm" accept=".txt,.fb2,.epub,.zip,.pdf" value={file} onChange={onPick} placeholder={t.file} disabled={uploading} clearable />
              <Text size="xs" c="dimmed" mt={-4}>{t.formatHint}</Text>
              {file && <TextInput size="sm" label={t.label} value={label} maxLength={120} disabled={uploading} onChange={(e) => setLabel(e.currentTarget.value)} />}
              {error && <Text size="xs" c="red">{String(error)}</Text>}
              {uploading && (
                <div className="h-1 w-full rounded-full overflow-hidden" style={{ background: 'var(--mantine-color-default-border)' }}>
                  <div className="h-1 rounded-full" style={{ width: `${progress}%`, background: 'var(--mantine-color-blue-filled)', transition: 'width 200ms' }} />
                </div>
              )}
              <Button size="sm" loading={uploading} disabled={!file || !!(file && precheck(file))} onClick={upload}>
                {uploading ? (progress < 100 ? `${t.uploadBtn} ${progress}%` : (language === 'ru' ? 'Обрабатываем…' : 'Processing…')) : t.uploadBtn}
              </Button>
              <Text size="xs" c="dimmed">{t.privacy}</Text>
            </div>
          )}
        </div>
      </Modal>
      <Modal opened={canShareBookUploads(myId) && !!shareFor} onClose={() => setShareFor(null)} title={t.shareTitle} centered size="sm" zIndex={1300}>
        {shareFor && <Text size="sm" c="dimmed" mb={8} lineClamp={2}>{shareFor.label || shareFor.originalFilename}</Text>}
        <TextInput size="sm" value={shareQuery} onChange={(e) => { setShareQuery(e.currentTarget.value); setShareState(null); }} placeholder={t.sharePick} data-autofocus />
        <div className="flex flex-col mt-2">
          {shareUsers.map((u) => (
            <button key={u.id} type="button" onClick={() => shareWith(u)} className="flex items-center gap-2 px-2 py-2 rounded text-left"
              style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', color: 'inherit' }}>
              {u.avatarUrl ? <img src={u.avatarUrl} alt="" width={28} height={28} style={{ borderRadius: 14, objectFit: 'cover' }} /> : <span style={{ width: 28, height: 28, borderRadius: 14, background: 'var(--mantine-color-default-hover)' }} />}
              <Text size="sm">{u.userName}</Text>
            </button>
          ))}
          {shareQuery.trim() && shareUsers.length === 0 && !shareState && <Text size="xs" c="dimmed" mt={4}>{t.shareNone}</Text>}
        </div>
        {shareState && shareState.done && <Text size="sm" c="teal" mt={8}>✓ {t.shareDone(shareState.done)}</Text>}
        {shareState && shareState.failed && <Text size="sm" c="red" mt={8}>{t.shareFail}</Text>}
      </Modal>
    </div>
  );
};

export default BookTexts;
